/* Dependencies */
import anime from 'animejs';

// Models
import { ProducersLoadMoreProps } from './ProducersLoadMore.model';

/**
 * Prducers Load More
 * This functionality is required to work around a limitation within
 * shopify where only 16 blocks can be loaded at a time within a section.
 * The client would like to add more than 16 so we allow them to add multiple Ul's
 * to the page and we move them to within a container to allow load more functionality.
 * @class
 */
class ProducersLoadMore {
  private isOpen = false;

  public producersElements: HTMLElement[];
  public loadMoreElement: HTMLElement;
  public containerElement: HTMLElement;
  public containerContentElement: HTMLElement;
  public producerList: HTMLElement;

  constructor(props: ProducersLoadMoreProps) {
    this.containerElement = props.containerElement;
    this.containerContentElement = props.containerContentElement;
    this.producersElements = props.producersElements;
    this.loadMoreElement = props.loadMoreElement;

    this.moveToContainer();
    this.setContainerMaxHeight();
    this.attachEvents();
  }

  /**
   * Moves the producer elements to a new container.
   */
  moveToContainer(): void {
    // Loop into the current elements.
    this.producersElements.forEach((element, index) => {
      // Remove current DOM elements.
      element.parentElement.removeChild(element);

      // Create one only element.
      if(index === 0) this.producerList = element; 
      else {
        element.querySelectorAll('li').forEach((li) => {
          this.producerList.appendChild(li);
        });
      }
    });

    // Order asc 
    Array.from(this.producerList.querySelectorAll('li'))
        .sort((a, b) => a.querySelector('.producer-list__title span').textContent.localeCompare(b.querySelector('.producer-list__title span').textContent))
        .forEach((li, index) => this.producerList.insertBefore(li, this.producerList.childNodes[index]));

    // Insert into container
    this.containerContentElement.appendChild(this.producerList);

    // Hide Button When Not Needed
    if (this.producerList.childElementCount <= 12) {
      this.hideLoadMoreAction();
    }

    // Set the max height of the container
    setTimeout(() => {
      // Short timeout to allow DOM time to update.
      this.setContainerMaxHeight();
    }, 300);
  }

  /**
   * Hides the load more element.
   */
  hideLoadMoreAction(): void {
    this.loadMoreElement.style.display = 'none';
  }

  /**
   * Sets 12 first element max height when closed.
   */
  setContainerMaxHeight(): void {
    var containerHeight = 0;
    Array.from(this.producerList.querySelectorAll('li')).slice(0, 12).map((listItem) => { containerHeight += listItem.offsetHeight });
    this.containerElement.style.maxHeight = `${containerHeight}px`;
  }

  /**
   * Animates the list open and shows all producers.
   */
  showAllProducers(): void {
    // Set to active
    this.isOpen = true;

    // Animate the list open.
    anime({
      targets: this.containerElement,
      maxHeight: [
        `${this.containerElement.clientHeight}px`,
        `${this.containerContentElement.clientHeight}px`,
      ],
      duration: 200,
      easing: 'linear',
      complete: () => {
        this.hideLoadMoreAction();
        // Remove the max-height once animated to make it responsive.
        this.containerElement.style.maxHeight = null;
      },
    });
  }

  /**
   * Attaches the relevant dom events.
   */
  attachEvents(): void {
    // Bind the load more event.
    if (this.loadMoreElement) {
      this.loadMoreElement.addEventListener('click', () => {
        if (!this.isOpen) {
          this.showAllProducers();
        }
      });
    }

    // Bind the resize event to resize the list.
    let resizeTimeout: NodeJS.Timeout;
    window.addEventListener('resize', () => {
      if (this.isOpen) return;
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.setContainerMaxHeight();
      }, 250);
    });
  }
}

/**
 * Initiate the producers load more functionality.
 */
export const producersLoadMoreInit = (): void => {
  // Detect the producers blocks
  const producersElements = Array.from(
    document.querySelectorAll('[data-producers-list]')
  ) as HTMLElement[];

  // Ingore if there are no producers blocks
  if (!producersElements.length) return;

  // Get the required elements.
  const loadMoreElement: HTMLElement = document.querySelector(
    '[data-producers-load-more]'
  );
  const containerElement: HTMLElement = document.querySelector(
    '[data-producers-container]'
  );
  const containerContentElement: HTMLElement = document.querySelector(
    '[data-producers-container-content]'
  );

  // Prevent load if intro is missing
  if (!containerElement) return;

  // Create a new ProducersLoadMore instance
  new ProducersLoadMore({
    containerElement,
    containerContentElement,
    producersElements,
    loadMoreElement,
  });
};
